import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Modal } from "~/components/Modal";
import { env } from "~/env.mjs";
import { useUser } from "~/modules/user/hooks/useUser";
import { Routes } from "~/routes";
import * as Analytics from "../../analytics";
import { InlineAuthenticationModalContent } from "./InlineAuthenticationModalContent";
import { useInlineAuth } from "../../hooks/useInlineAuth";
import { PAYWALL_EXCLUDED_PAGES } from "~/modules/billing/consts";

// // // //

interface Props {
    onAuthSuccess?: () => void;
}

function getInternalPageVisitCount(): number {
    if (typeof window === "undefined") return 0;
    const count = sessionStorage.getItem("internal-page-visit-count");
    return count ? parseInt(count, 10) : 0;
}

function incrementInternalPageVisitCount(): number {
    if (typeof window === "undefined") return 0;
    const currentCount = getInternalPageVisitCount();
    const newCount = currentCount + 1;
    sessionStorage.setItem("internal-page-visit-count", newCount.toString());
    return newCount;
}

const MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN =
    env.NEXT_PUBLIC_DEV_MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN ?? 5;

/**
 * InlineAuthenticationModal
 * Handles user authentication via Google or email/OTP verification
 * Self-manages visibility based on user state and feature flags
 */
export function InlineAuthenticationModal({ onAuthSuccess }: Props) {
    const router = useRouter();
    const { loading, isLoggedIn } = useUser();
    const isInitialLoad = loading;

    const {
        currentStep,
        isLoading,
        userEmail,
        handleEmailFormSubmit,
        handleGoogleSignIn,
        handleOTPSubmit,
    } = useInlineAuth({ onAuthSuccess });

    const [show, setShow] = useState(false);
    const [internalPageVisitCount, setInternalPageVisitCount] = useState(0);

    // Initialize page visit count from sessionStorage
    useEffect(() => {
        setInternalPageVisitCount(getInternalPageVisitCount());
    }, []);

    // Track internal page visits for authenticated users
    useEffect(() => {
        if (isLoggedIn || isInitialLoad) {
            return;
        }

        const isPublicPage = PAYWALL_EXCLUDED_PAGES.includes(
            router.pathname as Routes,
        );

        if (!isPublicPage) {
            const newCount = incrementInternalPageVisitCount();
            setInternalPageVisitCount(newCount);
        }
    }, [isLoggedIn, router.pathname, isInitialLoad]);

    // Determine if modal should show
    useEffect(() => {
        if (isInitialLoad || isLoggedIn) {
            setShow(false);
            return;
        }

        const isPublicPage = PAYWALL_EXCLUDED_PAGES.includes(
            router.pathname as Routes,
        );

        if (!isPublicPage) {
            const showModal = () => {
                Analytics.trackInlineAuthenticationModalOpen();
                setShow(true);
            };

            const showModalWithDelay = () => {
                // 5-second delay
                const delayTimer = setTimeout(showModal, 5000);
                return () => clearTimeout(delayTimer);
            };

            // Show modal after 5th visit
            if (
                internalPageVisitCount >= MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN
            ) {
                return showModalWithDelay();
            }
        } else {
            setShow(false);
        }
    }, [
        isLoggedIn,
        router.asPath,
        router.pathname,
        isInitialLoad,
        internalPageVisitCount,
    ]);

    return (
        <Modal
            show={show}
            allowClose={false}
            size="md"
            allowScroll={false}
            blurBackground={true}
            viewport="inset"
        >
            <InlineAuthenticationModalContent
                currentStep={currentStep}
                isLoading={isLoading}
                userEmail={userEmail}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSubmit={handleEmailFormSubmit}
                onOTPSubmit={handleOTPSubmit}
            />
        </Modal>
    );
}
